import type { Offer, Product } from "@/types";

export interface PriceResult {
  base: number;
  final: number;
  offerApplied: boolean;
  offerId: string | null;
  offerName: string | null;
  wholesale: boolean;
}

function toNumber(value: number | string | null | undefined, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** A date-only end_date should include the whole day. */
function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

export function isOfferCurrentlyActive(offer: Offer, now: Date = new Date()): boolean {
  if (!offer.active) return false;
  if (offer.start_date) {
    const start = new Date(offer.start_date);
    if (!Number.isNaN(start.getTime()) && now < start) return false;
  }
  if (offer.end_date) {
    const end = new Date(offer.end_date);
    if (!Number.isNaN(end.getTime()) && now > endOfDay(end)) return false;
  }
  return true;
}

export function findActiveOffer(
  offers: Offer[],
  product: Pick<Product, "id" | "category_id">,
  now: Date = new Date(),
): Offer | null {
  let match: Offer | null = null;
  for (const offer of offers) {
    if (!isOfferCurrentlyActive(offer, now)) continue;
    const appliesToProduct = offer.scope === "product" && offer.product_id === product.id;
    const appliesToCategory =
      offer.scope === "category" && !!product.category_id && offer.category_id === product.category_id;
    if (!appliesToProduct && !appliesToCategory) continue;
    // Prefer a product-specific offer over a category offer.
    if (!match || (appliesToProduct && match.scope !== "product")) {
      match = offer;
    }
  }
  return match;
}

export function applyOffer(base: number, offer: Offer | null): number {
  if (!offer) return round2(base);
  const value = toNumber(offer.discount_value, 0);
  if (offer.discount_type === "percentage") {
    return round2(Math.max(base - (base * value) / 100, 0));
  }
  if (offer.discount_type === "fixed") {
    return round2(Math.max(base - value, 0));
  }
  if (offer.discount_type === "special_price") {
    const special = toNumber(offer.special_price, 0);
    return round2(special > 0 ? special : base);
  }
  return round2(base);
}

/** Resolves the unit price for a product given the customer type and active offers. */
export function resolvePrice(
  product: Pick<Product, "price" | "wholesale_price" | "id" | "category_id">,
  customerType: string | null | undefined,
  offers: Offer[],
): PriceResult {
  const regular = toNumber(product.price, 0);
  const wholesalePrice = toNumber(product.wholesale_price, 0);
  const isWholesale = customerType === "mayorista" && wholesalePrice > 0;
  const base = isWholesale ? wholesalePrice : regular;
  const offer = findActiveOffer(offers, product);
  const final = applyOffer(base, offer);
  return {
    base: round2(base),
    final,
    offerApplied: !!offer && final < base,
    offerId: offer?.id ?? null,
    offerName: offer?.name ?? null,
    wholesale: isWholesale,
  };
}